/**
 * IHS-7 Goals — total / per-match / conceded comparison per team.
 *
 * The per-game rows now render through the shared `StatComparisonRow`
 * primitive (the "ledger row") instead of a hand-rolled copy of it. That
 * primitive's docstring always claimed it collapsed this widget's row
 * layout; until 2026-07-09 it did not, which is why IhsGoals kept the old
 * centred/divider look after the v4 redesign shipped.
 *
 * Leader semantics are computed here because they are stat-specific:
 *   scored / game   → MORE is better
 *   conceded / game → LESS is better
 * The engine ships no `lead` for this widget, and the renderer must not
 * invent one from nothing — but a strict numeric comparison of two values
 * it already holds is presentation, not fabrication.
 */

import type { ReactElement } from 'react';
import { useTranslation } from '../../../i18n/index.js';
import { useTenantConfig } from '../../../tenant/useTenantConfig.js';
import type { IhsGoalsData } from '../../../types/ihs.js';
import { formatDecimalAvg } from '../../../utils/numbers.js';
import { IhsCard, dashable } from './IhsCard.js';
import { GoalNetSvg } from './IhsPrimitives.js';
import { TeamCrest } from '../../primitives/TeamCrest.js';
import { StatComparisonRow } from '../../primitives/StatComparisonRow.js';
import styles from './IhsGoals.module.css';

type Lead = 'home' | 'away' | 'both';

/**
 * Which side leads a two-value comparison. `higherWins: false` inverts the
 * test for "less is better" stats such as goals conceded. Returns undefined
 * when either value is missing — no verdict without data.
 */
function leadOf(
  home: number | null,
  away: number | null,
  higherWins: boolean,
): Lead | undefined {
  if (home === null || away === null) return undefined;
  if (home === away) return 'both';
  const homeWins = higherWins ? home > away : home < away;
  return homeWins ? 'home' : 'away';
}

export function IhsGoals({ data }: { data: IhsGoalsData }): ReactElement {
  const { t } = useTranslation();
  const tenant = useTenantConfig();
  const decimalSeparator =
    tenant.kind === 'live' || tenant.kind === 'paused'
      ? tenant.config.market.decimalSeparator
      : '.';

  const fmtAvg = (n: number | null): string =>
    formatDecimalAvg(n, decimalSeparator);

  const title =
    data.header?.title ??
    t('ihs.goals.title', { defaultValue: 'Goals' });
  const subtitle = data.header?.subtitle;

  const scoredLead = leadOf(data.home.scoredPerGame, data.away.scoredPerGame, true);
  const concededLead = leadOf(
    data.home.concededPerGame,
    data.away.concededPerGame,
    false,
  );

  // League-average marker (2026-08-01) — engine ships per-match means.
  const leagueAvg = (data as { leagueAvg?: Record<string, number> }).leagueAvg ?? {};
  const avgProps = (key: string): Record<string, unknown> => {
    const v = leagueAvg[key];
    return typeof v === 'number' && Number.isFinite(v)
      ? {
          leagueAvg: v,
          leagueAvgLabel: t('stat.leagueAvg', {
            defaultValue: 'Lg avg {{value}}',
            value: fmtAvg(v),
          }),
        }
      : {};
  };

  return (
    <IhsCard title={title} {...(subtitle !== undefined ? { subtitle } : {})}>
      {/* Row 1 — total goals with crests and goal-net divider */}
      <div className={`${styles.totalRow} stage-row`}>
        <div className={styles.totalSide}>
          <TeamCrest
            {...(data.home.team.badge !== undefined ? { src: data.home.team.badge } : {})}
            name={data.home.team.name}
            size={32}
          />
          <span className={styles.totalValue}>{dashable(data.home.totalGoals)}</span>
        </div>
        <div className={styles.totalCenter}>
          <GoalNetSvg width={64} />
          <span className={styles.totalLabel}>
            {t('ihs.goals.total', { defaultValue: 'Goals' })}
          </span>
        </div>
        <div className={styles.totalSide}>
          <span className={styles.totalValue}>{dashable(data.away.totalGoals)}</span>
          <TeamCrest
            {...(data.away.team.badge !== undefined ? { src: data.away.team.badge } : {})}
            name={data.away.team.name}
            size={32}
          />
        </div>
      </div>

      {/* Rows 2-3 — the shared Stat Row v2. Per-game averages are plain
          counts, not shares — renderType='numbers' (the automatic
          value-parsed bar is retired; bars are explicit-ask only). */}
      <StatComparisonRow
        label={t('ihs.goals.perMatch', { defaultValue: 'Scored / Game' })}
        home={fmtAvg(data.home.scoredPerGame)}
        away={fmtAvg(data.away.scoredPerGame)}
        {...avgProps('scoredPerGame')}
        renderType="numbers"
        {...(scoredLead !== undefined ? { lead: scoredLead } : {})}
        animateOnInView
      />
      <StatComparisonRow
        label={t('ihs.goals.conceded', { defaultValue: 'Conceded / Game' })}
        home={fmtAvg(data.home.concededPerGame)}
        away={fmtAvg(data.away.concededPerGame)}
        {...avgProps('concededPerGame')}
        renderType="numbers"
        {...(concededLead !== undefined ? { lead: concededLead } : {})}
        animateOnInView
      />
    </IhsCard>
  );
}
